import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Group } from '@visx/group';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import ChartTooltip from '../../components/ChartTooltip';
import MosaicChartImage from '../../components/ChartsImages/MosaicChartImage';
import { scaleLinear } from '@visx/scale';
import {
  FormatUtils,
  getColors,
  METRICS,
  useWindowSize,
  getPopulationPerc,
  MAP_CHART_FIELDS_LABEL
} from '../../utils';
import styled from 'styled-components';
import uniqid from 'uniqid';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';

type StyledMosaicChartProps = {
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  isScrollable?: boolean;
};
const StyledMosaicChartWrapper = styled.div<StyledMosaicChartProps>`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  overflow: ${(props) => (props.isScrollable ? 'hidden auto' : 'hidden')};
  z-index: 0;
`;

const StyledMosaicChartSvg = styled.svg`
  z-index: 1;
`;

type MosaicChartProps = {
  margin: any;
  data: any[];
  color: string;
  emptyDataMessage: string;
  isScrollable: boolean;
  showItemsPreview: boolean;
  metric?: string;
  numColumns?: number;
  numRows?: number;
};

let tooltipTimeout: number;
const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 };

const MosaicChart: FC<MosaicChartProps> = ({
  margin = defaultMargin,
  data,
  color,
  emptyDataMessage,
  isScrollable,
  showItemsPreview,
  metric,
  numColumns = 5,
  numRows = 5
}) => {
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );
  const columnWidth = Math.max(
    (width - margin.left - margin.right) / numColumns,
    0
  );
  const rowHeight = Math.max(
    (height - margin.bottom - margin.top) / numRows,
    0
  );

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const isEmpty = !data || data.length === 0;

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, index) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      if (!svgRef.current) return;
      const eventSvgCoords = localPoint(svgRef.current, event);
      showTooltip({
        tooltipData: {
          ...data[index]
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, data]
  );

  const colorScale = useMemo(
    () =>
      scaleLinear<string>({
        domain: [0, isEmpty ? 0 : data.length / 2, isEmpty ? 0 : data.length],
        range: getColors(color)
      }),
    [data, isEmpty, color]
  );

  if (isEmpty) {
    return (
      <StyledMosaicChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledMosaicChartWrapper>
    );
  }

  return (
    <StyledMosaicChartWrapper ref={chartRef} isScrollable={isScrollable}>
      <StyledMosaicChartSvg
        ref={svgRef}
        width={width}
        height={
          Math.floor((data.length - 1) / numColumns) * rowHeight + rowHeight
        }
      >
        <rect width={width} height={height} fill='#ffffff' />
        <Group
          top={margin.top}
          left={margin.left}
          key={`group-${chartId.current}`}
        >
          <rect
            fill='#ffffff'
            stroke='#9493A6'
            strokeWidth='1'
            width={xMax}
            height={yMax}
          />
          {data.map((item, index) => {
            const columnIndex = index % numColumns;
            const rowIndex = Math.floor(index / numColumns);

            const id = `mosaic-${chartId.current}-item-icon-${index}-${rowIndex}-${columnIndex}`;

            return (
              <MosaicChartImage
                key={`mosaic-${chartId.current}-item-${index}-${rowIndex}-${columnIndex}`}
                id={id}
                pic={item.pic}
                label={item.label}
                width={columnWidth}
                height={rowHeight}
                metric={
                  metric &&
                  `${
                    MAP_CHART_FIELDS_LABEL[metric]
                  } ${FormatUtils.formattedValue(item[metric], metric)}`
                }
                x={columnIndex * columnWidth}
                y={rowIndex * rowHeight}
                color={colorScale(index)}
                showItemsPreview={showItemsPreview}
                onMouseMove={(event: any) => handleMouseMove(event, index)}
                onMouseLeave={(event: any) => handleMouseLeave(event)}
              />
            );
          })}
        </Group>
      </StyledMosaicChartSvg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          <div>
            <span>Reach: </span>
            {FormatUtils.formattedValue(
              getPopulationPerc(tooltipData),
              METRICS.REACH
            )}
          </div>
          <div>
            <span>Affinity: </span>
            {FormatUtils.formattedValue(
              tooltipData.target_audience_affinity,
              METRICS.AFFINITY
            )}
          </div>
          <div>
            <span>Base: </span>
            {FormatUtils.formattedValue(
              tooltipData.base_population_perc,
              METRICS.BASE
            )}
          </div>
          <div>
            <span>Penetration: </span>
            {FormatUtils.formattedValue(
              tooltipData.penetration,
              METRICS.PENETRATION
            )}
          </div>
        </ChartTooltip>
      )}
    </StyledMosaicChartWrapper>
  );
};

export default MosaicChart;
